import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchFacets, search } from './api';
import type { Facets, SearchHit, SearchResults } from './api';
import type { Route } from './routes';

/**
 * Search every meeting, by who said it.
 *
 * THE QUERY THIS PRODUCT EXISTS TO ANSWER. Everywhere else, "what did Alex
 * commit to" means asking a diarisation model which cluster of voice segments
 * was probably Alex. Here the speaker filter is an equality test on the account
 * the identity provider signed a token for, so it is exact — and the chip says
 * so rather than leaving it as a detail in a blog post.
 *
 * Every hit is a citation: clicking one opens that meeting at that second, with
 * the line highlighted, and the URL it lands on can be pasted to somebody else.
 * An answer you cannot check is a rumour with a citation style.
 */

/** How long to wait after a keystroke before asking the server. */
const DEBOUNCE_MS = 250;

const RANGES: { label: string; days?: number }[] = [
  { label: 'any time' },
  { label: 'last 30 days', days: 30 },
  { label: 'last 90 days', days: 90 },
  { label: 'last 9 months', days: 274 },
];

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Wrap every occurrence of a matched term in a <mark>.
 *
 * The terms come from the SERVER's response rather than from the input box, so
 * what is highlighted is exactly what was matched. Re-tokenising the query here
 * would eventually disagree with the engine in some small way, and a result
 * that highlights nothing is a result the reader distrusts.
 */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  const parts = useMemo(() => {
    // An empty term would match at index 0 and consume nothing, so the loop
    // below would never advance. The server does not send one, and this is the
    // guard that means it could not hang a browser if it ever did.
    const sorted = terms.filter((t) => t.length > 0).sort((a, b) => b.length - a.length);
    if (sorted.length === 0) return [{ text, hit: false }];

    const out: { text: string; hit: boolean }[] = [];
    let rest = text;
    while (rest.length > 0) {
      const lower = rest.toLowerCase();
      let best = -1;
      let bestTerm = '';
      for (const t of sorted) {
        const i = lower.indexOf(t);
        if (i >= 0 && (best === -1 || i < best)) {
          best = i;
          bestTerm = t;
        }
      }
      if (best === -1) {
        out.push({ text: rest, hit: false });
        break;
      }
      if (best > 0) out.push({ text: rest.slice(0, best), hit: false });
      out.push({ text: rest.slice(best, best + bestTerm.length), hit: true });
      rest = rest.slice(best + bestTerm.length);
    }
    return out;
    // Longest term first, so "pricing" wins over a shorter term that also
    // matched inside it and the highlight covers the whole word.
  }, [text, terms]);

  return (
    <>
      {parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}
    </>
  );
}

function Hit({ hit, terms, onOpen }: { hit: SearchHit; terms: string[]; onOpen: () => void }) {
  return (
    <article className="mx-hit">
      <button onClick={onOpen}>
        <p className="mx-hit-meta">
          <b>{hit.title || hit.room}</b>
          <span className="mx-dotsep">·</span>
          {when(hit.startedAt)}
          <span className="mx-dotsep">·</span>
          <span className="mx-hit-at">{clock(hit.at)}</span>
          <span className="mx-dotsep">·</span>
          <span className="mx-hit-speaker">{hit.speaker}</span>
        </p>
        <p className="mx-hit-quote">
          <Highlighted text={hit.text} terms={terms} />
        </p>
      </button>
    </article>
  );
}

export function Search({
  initial,
  navigate,
}: {
  initial: { q?: string; speaker?: string; room?: string };
  navigate: (r: Route) => void;
}) {
  const [q, setQ] = useState(initial.q ?? '');
  const [speaker, setSpeaker] = useState(initial.speaker ?? '');
  const [room, setRoom] = useState(initial.room ?? '');
  const [days, setDays] = useState<number | undefined>(undefined);

  const [facets, setFacets] = useState<Facets | null>(null);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which request is current. A slow query for "p" must not overwrite the
  // results for "pricing" when it finally lands — the classic search race, and
  // the one that makes a search box feel haunted.
  const generation = useRef(0);

  useEffect(() => {
    fetchFacets()
      .then(setFacets)
      .catch(() => setFacets({ speakers: [], rooms: [] }));
  }, []);

  const run = useCallback(
    (query: string, sp: string, rm: string, d: number | undefined) => {
      if (!query.trim() && !sp) {
        setResults(null);
        setBusy(false);
        return;
      }
      const mine = ++generation.current;
      setBusy(true);
      search({ q: query, speaker: sp, room: rm, days: d })
        .then((r) => {
          if (mine !== generation.current) return;
          setResults(r);
          setError(null);
        })
        .catch((e: unknown) => {
          if (mine !== generation.current) return;
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (mine === generation.current) setBusy(false);
        });
    },
    [],
  );

  // Debounced on the query, immediate on a filter: typing is a stream of
  // intermediate states, clicking a chip is a decision.
  useEffect(() => {
    const t = setTimeout(() => run(q, speaker, room, days), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, speaker, room, days, run]);

  // The URL follows the query, so a search can be sent to somebody. Replace
  // rather than push: typing eight characters should not cost eight presses of
  // the back button.
  useEffect(() => {
    const href = `/search?${new URLSearchParams(
      Object.entries({ q, speaker, room }).filter(([, v]) => v) as [string, string][],
    )}`;
    window.history.replaceState({}, '', href);
  }, [q, speaker, room]);

  const speakerName =
    facets?.speakers.find((s) => s.principal === speaker)?.name ?? speaker;

  // The frame supplies the bar, the nav and the account menu (AppShell); this
  // page is its own content only.
  return (
    <>
      <div className="mx-search-bar">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search everything that was said"
          aria-label="Search transcripts"
          spellCheck={false}
        />
        {q && (
          <button className="mx-search-clear" onClick={() => setQ('')} aria-label="Clear">
            ×
          </button>
        )}
      </div>

      <div className="mx-chips">
        {/* The speaker filter is the whole point, so it goes first and it
            names a person rather than offering a text box: the value behind
            it is a principal, and typing a name would reintroduce exactly the
            ambiguity per-track recording removed. */}
        <select
          className={`mx-chip mx-chip-select${speaker ? ' on' : ''}`}
          value={speaker}
          onChange={(e) => setSpeaker(e.target.value)}
          aria-label="Filter by speaker"
        >
          <option value="">anyone</option>
          {(facets?.speakers ?? []).map((s) => (
            <option key={s.principal} value={s.principal}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          className={`mx-chip mx-chip-select${room ? ' on' : ''}`}
          value={room}
          onChange={(e) => setRoom(e.target.value)}
          aria-label="Filter by room"
        >
          <option value="">all rooms</option>
          {(facets?.rooms ?? []).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        {RANGES.map((r) => (
          <button
            key={r.label}
            className={`mx-chip${days === r.days ? ' on' : ''}`}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error && <p className="mx-error">{error}</p>}

      {!results && !error && (
        <p className="mx-muted mx-search-hint">
          {speaker
            ? `Showing everything ${speakerName} said. Add words to narrow it.`
            : 'Type to search every transcript, or pick a speaker to read everything one person said.'}
        </p>
      )}

      {results && results.hits.length === 0 && (
        <p className="mx-muted mx-search-hint">
          Nothing matched. {results.scanned} meeting{results.scanned === 1 ? '' : 's'} searched.
        </p>
      )}

      {results && results.hits.length > 0 && (
        <>
          <div className="mx-hits">
            {results.hits.map((h, i) => (
              <Hit
                key={`${h.room}/${h.session}/${h.at}/${i}`}
                hit={h}
                terms={results.terms ?? []}
                onOpen={() =>
                  navigate({
                    name: 'meeting',
                    room: h.room,
                    session: h.session,
                    at: h.at,
                  })
                }
              />
            ))}
          </div>
          <p className="mx-search-foot">
            <span>
              {results.lines} line{results.lines === 1 ? '' : 's'} · {results.meetings} meeting
              {results.meetings === 1 ? '' : 's'} · {(results.tookMs / 1000).toFixed(2)} s
              {results.truncated && ` · showing the first ${results.hits.length}`}
            </span>
            {/* Not a boast about speed — a statement about where the search
                ran. It is the difference the product is selling, and this is
                the one screen where a user can see it being true. */}
            <span>index: local · 0 external requests</span>
          </p>
        </>
      )}

      {busy && <span className="sr-only">Searching…</span>}
    </>
  );
}
